import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Checkbox,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import Swal from 'sweetalert2';
import PrintIcon from '@mui/icons-material/Print';

import { insurancetypes, claimmonths, claimsDetailsall, diagnosis_tarf, investigationReport, medicationReport ,procedureReport, claimsXmlExport } from '../BaseURL';
//  frequencyMap went with convertFrequency — the server maps frequencies now.
import { formatWithOutSom } from '../Message';

// Styled components
const HeaderBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#11998e',
  color: 'white',
  padding: '8px 16px',
  borderRadius: '4px 4px 0 0',
  fontSize: '14px',
  fontWeight: 500
}));

const FilterSection = styled(Box)(({ theme }) => ({
  padding: '16px',
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  backgroundColor: '#F3F4F6'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1E40AF',
  color: 'white',
  padding: '8px 24px',
  '&:hover': {
    backgroundColor: '#1E3A8A'
  }
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#7C3AED',
  color: 'white',
  padding: '8px 24px',
  '&:hover': {
    backgroundColor: '#6D28D9'
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '12px',
  padding: '8px',
  borderRight: '1px solid #E5E7EB',
  '&:last-child': {
    borderRight: 'none'
  }
}));

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  fontSize: '12px',
  padding: '8px',
  fontWeight: 600,
  backgroundColor: '#D1D5DB',
  borderRight: '1px solid #9CA3AF',
  '&:last-child': {
    borderRight: 'none'
  }
}));

const ClaimsReport = () => {
  // State management
  const [insuranceType, setInsuranceType] = useState('');
  const [monthYear, setMonthYear] = useState('');
  const [insuranceTypes, setInsuranceTypes] = useState([]);
  const [claimMonths, setClaimMonths] = useState([]);
  const [claimsData, setClaimsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingClaim, setGeneratingClaim] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedRows, setSelectedRows] = useState([]);

  const [xmlStartDate, setXmlStartDate] = useState('');
  const [xmlEndDate, setXmlEndDate]     = useState('');

  // Fetch insurance types on component mount
  useEffect(() => {
    fetchInsuranceTypes();
    fetchClaimMonths();
  }, []);

  const fetchInsuranceTypes = async () => {
    try {
      const response = await axios.get(insurancetypes);
      setInsuranceTypes(response.data);
    } catch (err) {
      console.error('Error fetching insurance types:', err);
      setError('Failed to load insurance types');
    }
  };

  const fetchClaimMonths = async () => {
    try {
      const response = await axios.get(claimmonths);
      setClaimMonths(response.data);
    } catch (err) {
      console.error('Error fetching claim months:', err);
      setError('Failed to load claim months');
    }
  };

  const fetchClaimsData = async () => {
    if (!insuranceType || !monthYear) {
      setError('Please select both Insurance Type and Month/Year');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(claimsDetailsall, {
        params: {
          insuranceType,
          monthYear
        }
      });
      setClaimsData(response.data);
      setPage(0);
    } catch (err) {
      console.error('Error fetching claims data:', err);
      setError('Failed to load claims data');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  //  escapeXml lived here for the in-browser XML builder. That builder is gone —
  //  the server writes the NHIA file now — so nothing in this component escapes
  //  XML any more. Deleted rather than left behind: a spare escaper is an
  //  invitation to hand-roll a second exporter, which is the mistake that put
  //  456 rejected claims in the September report.

  // Function to fetch individual claim data
  const fetchIndividualClaimData = async (patientId, insuranceStatus, visitDate, dischargeDate) => {
    try {
      console.log('Fetching claim data for:', { patientId, insuranceStatus, visitDate, dischargeDate });

      // Fetch diagnosis data
      const diagnosisResponse = await axios.get(diagnosis_tarf, {
        params: {
          patient_id: patientId,
          start_date: visitDate,
          end_date: dischargeDate
        }
      });

      // Fetch procedure data
      const procedureResponse = await axios.get(procedureReport, {
        params: {
          patient_id: patientId,
          start_date: visitDate,
          end_date: dischargeDate
        }
      });

const procedure = Array.isArray(procedureResponse.data)
  ? procedureResponse.data
  : (procedureResponse.data.data || []);

      // Fetch investigation data
      const investigationResponse = await axios.get(investigationReport, {
        params: {
          patient_id: patientId,
          start_date: visitDate,
          end_date: dischargeDate,
          status: insuranceStatus
        }
      });

      // Fetch medication data
      const medicationResponse = await axios.get(medicationReport, {
        params: {
          patient_id: patientId,
          drug_status: insuranceStatus,
          start_date: visitDate,
          end_date: dischargeDate
        }
      });

      // Handle both direct array and wrapped response formats
      const diagnosis = Array.isArray(diagnosisResponse.data) 
        ? diagnosisResponse.data 
        : (diagnosisResponse.data.data || []);

      
      const investigation = Array.isArray(investigationResponse.data) 
        ? investigationResponse.data 
        : (investigationResponse.data.data || []);
      
      const medication = Array.isArray(medicationResponse.data) 
        ? medicationResponse.data 
        : (medicationResponse.data.data || []);

      return {
        diagnosis,
        investigation,
        medication ,
         procedure 
      };
    } catch (err) {
      console.error('Error fetching individual claim data:', err);
      console.error('Error details:', err.response?.data || err.message);
      throw err;
    }
  };


  // Generate HTML for a single claim
  const generateClaimHTML = (row, claimData, age, fullName) => {
    // Calculate totals
    let pharmacyTotal = 0;
    claimData.medication.forEach(med => {
      pharmacyTotal += (parseFloat(med.unit_cost || 0) * parseFloat(med.qua || 0));
    });

    let investigationTotal = 0;
    claimData.investigation.forEach(inv => {
      investigationTotal += parseFloat(inv.price || 0);
    });

    const serviceTotal = parseFloat(row.main_status || 0);
    const grandTotal = pharmacyTotal + investigationTotal + serviceTotal;

    // Generate diagnosis rows
    const diagnosisRows = claimData.diagnosis.map((diag, index) => `
      <tr>
        <td style="padding: 4px; border: 1px solid #000; font-size: 10px;">${diag.result || ''}</td>
        <td style="padding: 4px; border: 1px solid #000; font-size: 10px;">${diag.icd || ''}</td>
        <td style="padding: 4px; border: 1px solid #000; font-size: 10px;">${diag.gdrg || ''}</td>
      </tr>
    `).join('');

    // Generate investigation rows
    const investigationRows = claimData.investigation.map((inv, index) => `
      <tr>
        <td style="padding: 4px; border: 1px solid #000; font-size: 10px;">${inv.des || ''}</td>
        <td style="padding: 4px; border: 1px solid #000; font-size: 10px; text-align: right;">${formatWithOutSom(parseFloat(inv.price || 0))}</td>
      </tr>
    `).join('');

    // Generate medication rows
    const medicationRows = claimData.medication.map((med, index) => {
      const total = parseFloat(med.unit_cost || 0) * parseFloat(med.qua || 0);
      return `
        <tr>
          <td style="padding: 4px; border: 1px solid #000; font-size: 10px;">${index + 1}</td>
          <td style="padding: 4px; border: 1px solid #000; font-size: 10px;">${med.name || ''}</td>
          <td style="padding: 4px; border: 1px solid #000; font-size: 10px; text-align: right;">${formatWithOutSom(parseFloat(med.unit_cost || 0))}</td>
          <td style="padding: 4px; border: 1px solid #000; font-size: 10px; text-align: center;">${med.qua || ''}</td>
          <td style="padding: 4px; border: 1px solid #000; font-size: 10px; text-align: right;">${formatWithOutSom(total)}</td>
          <td style="padding: 4px; border: 1px solid #000; font-size: 10px;">${med.dat || ''}</td>
        </tr>
      `;
    }).join('');

    return `
      <div class="claim-page" style="page-break-after: always; padding: 20px;">
        <div class="header">
          <h2><strong>${insuranceType}</strong></h2>
          <p>Form No: ${row.code || ''}</p>
          <h2>JITROCK HOSPITAL</h2>
          <p><strong>Claim</strong></p>
          <p>Scheme - Month of ${monthYear}</p>
        </div>

        <div class="claim-info">
          <h3 style="margin-top: 0; margin-bottom: 10px; font-size: 12px;">Client Information</h3>
          <div class="info-row">
            <div class="info-label">Surname:</div>
            <div class="info-value">${row.surname || ''}</div>
            <div class="info-label">Other Name(s):</div>
            <div class="info-value">${row.othername || ''}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Record No:</div>
            <div class="info-value">${row.patient_id || ''}</div>
            <div class="info-label">Date of Birth:</div>
            <div class="info-value">${row.dob || ''}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Gender:</div>
            <div class="info-value">${row.gender || ''}</div>
            <div class="info-label">Age:</div>
            <div class="info-value">${age} Year(s)</div>
          </div>
          <div class="info-row">
            <div class="info-label">NHIS Number:</div>
            <div class="info-value">${row.insurance_no || ''}</div>
            <div class="info-label">Date of Claim:</div>
            <div class="info-value">${new Date().toISOString().split('T')[0]}</div>
          </div>
        </div>

        <div class="section-title">Services (to be filled in by all health care providers)</div>
        <div style="border: 1px solid #000; padding: 10px; margin-bottom: 15px;">
          <div class="info-row">
            <div class="info-label">Type of Attendance:</div>
            <div class="info-value">${row.which || 'Emergency/Acute episode'}</div>
          </div>
          <div class="info-row">
            <div class="info-label">1st Visit/Admission:</div>
            <div class="info-value">${row.dat || ''}</div>
            <div class="info-label">2nd Visit/Discharge:</div>
            <div class="info-value">${row.d_dat || ''}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Physician/Clinician Name:</div>
            <div class="info-value">${row.physician_name || ''}</div>
          </div>
        </div>

        <div class="section-title">Diagnosis(es)</div>
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>ICD-10</th>
              <th>G-DRG</th>
            </tr>
          </thead>
          <tbody>
            ${diagnosisRows || '<tr><td colspan="3" style="text-align: center;">No diagnosis records</td></tr>'}
          </tbody>
        </table>

        <div class="section-title">Investigation</div>
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            ${investigationRows || '<tr><td colspan="2" style="text-align: center;">No investigation records</td></tr>'}
          </tbody>
        </table>

        <div class="section-title">Medicines</div>
        <table>
          <thead>
            <tr>
              <th style="width: 5%;">#</th>
              <th>Description</th>
              <th style="width: 12%;">U.P</th>
              <th style="width: 10%;">Qty</th>
              <th style="width: 12%;">Total</th>
              <th style="width: 12%;">Date</th>
            </tr>
          </thead>
          <tbody>
            ${medicationRows || '<tr><td colspan="6" style="text-align: center;">No medication records</td></tr>'}
          </tbody>
        </table>

        <div class="section-title">Client Claim Summary</div>
        <table class="summary-table">
          <thead>
            <tr>
              <th>Type Of Service</th>
              <th>Tariff Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Service</td>
              <td style="text-align: right;">${formatWithOutSom(serviceTotal)}</td>
            </tr>
            <tr>
              <td>Investigation</td>
              <td style="text-align: right;">${formatWithOutSom(investigationTotal)}</td>
            </tr>
            <tr>
              <td>Pharmacy</td>
              <td style="text-align: right;">${formatWithOutSom(pharmacyTotal)}</td>
            </tr>
            <tr style="font-weight: bold; background-color: #E5E7EB;">
              <td>Total</td>
              <td style="text-align: right;">${formatWithOutSom(grandTotal)}</td>
            </tr>
          </tbody>
        </table>

        <div style="margin-top: 30px; font-size: 10px;">
          <p>Generated on: ${new Date().toLocaleString()}</p>
        </div>
      </div>
    `;
  };

  // Function to generate individual claim report
  const handleGenerateIndividualClaim = async (row) => {
    try {
      setGeneratingClaim(true);
      
      const claimData = await fetchIndividualClaimData(
        row.patient_id,
        row.scheme_name,
        row.dat,
        row.d_dat
      );

      const claimWindow = window.open('', '_blank', 'width=900,height=1200');

      if (claimWindow) {
        const age = calculateAge(row.dob);
        const fullName = `${row.surname || ''} ${row.othername || ''}`.trim();
        const claimHTML = generateClaimHTML(row, claimData, age, fullName);

        claimWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Claim Form - ${fullName}</title>
              <style>
                @page { size: A4; margin: 10mm; }
                body { font-family: Arial, sans-serif; margin: 0; padding: 0; font-size: 11px; }
                .header { text-align: center; margin-bottom: 15px; }
                .header h2 { margin: 5px 0; font-size: 16px; }
                .header p { margin: 3px 0; font-size: 11px; }
                .claim-info { border: 2px solid #000; padding: 10px; margin-bottom: 15px; }
                .info-row { display: flex; margin-bottom: 8px; }
                .info-label { font-weight: bold; width: 150px; }
                .info-value { flex: 1; }
                table { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 15px; }
                th { background-color: #D1D5DB; padding: 6px; border: 1px solid #000; font-size: 10px; font-weight: bold; text-align: left; }
                td { padding: 4px; border: 1px solid #000; font-size: 10px; }
                .section-title { font-weight: bold; background-color: #E5E7EB; padding: 6px; margin-top: 15px; margin-bottom: 5px; border: 1px solid #000; font-size: 11px; }
                .summary-table { width: 50%; margin-left: auto; }
                .print-button { position: fixed; top: 20px; right: 20px; padding: 10px 20px; background-color: #1E40AF; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; z-index: 1000; }
                .print-button:hover { background-color: #1E3A8A; }
                @media print { .print-button { display: none; } body { margin: 0; padding: 0; } .claim-page { padding: 10px !important; } }
              </style>
            </head>
            <body>
              <button class="print-button" onclick="window.print()">🖨️ Print Claim</button>
              ${claimHTML}
            </body>
          </html>
        `);
        claimWindow.document.close();
      } else {
        setError('Unable to open new window. Please check your browser settings.');
      }
    } catch (err) {
      console.error('Error generating individual claim:', err);
      setError(`Failed to generate individual claim report: ${err.message}`);
    } finally {
      setGeneratingClaim(false);
    }
  };

  // Function to preview multiple selected claims
  const handlePreviewSelectedClaims = async () => {
    if (selectedRows.length === 0) {
      setError('Please select at least one claim to preview');
      return;
    }

    const claimWindow = window.open('', '_blank', 'width=900,height=1200');
    
    if (!claimWindow) {
      setError('Unable to open new window. Please check your browser pop-up blocker settings.');
      return;
    }

    claimWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Loading Claims - ${insuranceType} - ${monthYear}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; text-align: center; }
            .loader { border: 5px solid #f3f3f3; border-top: 5px solid #1E40AF; border-radius: 50%; width: 50px; height: 50px; animation: spin 1s linear infinite; margin: 20px auto; }
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            .progress { margin-top: 20px; font-size: 16px; color: #666; }
          </style>
        </head>
        <body>
          <h2>Generating ${selectedRows.length} Claims...</h2>
          <div class="loader"></div>
          <div class="progress" id="progress">Processing claim 0 of ${selectedRows.length}</div>
        </body>
      </html>
    `);
    claimWindow.document.close();

    try {
      setGeneratingClaim(true);
      setError(null);

      const selectedClaimsData = claimsData.filter(row => selectedRows.includes(row.code));
      const claimsWithData = [];
      
      for (let i = 0; i < selectedClaimsData.length; i++) {
        const row = selectedClaimsData[i];
        
        const progressEl = claimWindow.document.getElementById('progress');
        if (progressEl) {
          progressEl.textContent = `Processing claim ${i + 1} of ${selectedClaimsData.length}`;
        }
        
        try {
          const claimData = await fetchIndividualClaimData(
            row.patient_id,
            row.scheme_name,
            row.dat,
            row.d_dat
          );
          claimsWithData.push({ row, claimData });
        } catch (error) {
          console.error(`Error fetching data for claim ${row.code}:`, error);
          claimsWithData.push({ 
            row, 
            claimData: { diagnosis: [], investigation: [], medication: [] } 
          });
        }
      }

      const allClaimsHTML = claimsWithData.map(({ row, claimData }) => {
        const age = calculateAge(row.dob);
        const fullName = `${row.surname || ''} ${row.othername || ''}`.trim();
        return generateClaimHTML(row, claimData, age, fullName);
      }).join('');

      claimWindow.document.open();
      claimWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Multiple Claims - ${insuranceType} - ${monthYear}</title>
            <style>
              @page { size: A4; margin: 10mm; }
              body { font-family: Arial, sans-serif; margin: 0; padding: 0; font-size: 11px; }
              .header { text-align: center; margin-bottom: 15px; }
              .header h2 { margin: 5px 0; font-size: 16px; }
              .header p { margin: 3px 0; font-size: 11px; }
              .claim-info { border: 2px solid #000; padding: 10px; margin-bottom: 15px; }
              .info-row { display: flex; margin-bottom: 8px; }
              .info-label { font-weight: bold; width: 150px; }
              .info-value { flex: 1; }
              table { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 15px; }
              th { background-color: #D1D5DB; padding: 6px; border: 1px solid #000; font-size: 10px; font-weight: bold; text-align: left; }
              td { padding: 4px; border: 1px solid #000; font-size: 10px; }
              .section-title { font-weight: bold; background-color: #E5E7EB; padding: 6px; margin-top: 15px; margin-bottom: 5px; border: 1px solid #000; font-size: 11px; }
              .summary-table { width: 50%; margin-left: auto; }
              .print-button { position: fixed; top: 20px; right: 20px; padding: 10px 20px; background-color: #1E40AF; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; z-index: 1000; }
              .print-button:hover { background-color: #1E3A8A; }
              @media print { .print-button { display: none; } body { margin: 0; padding: 0; } .claim-page { padding: 10px !important; } }
            </style>
          </head>
          <body>
            <button class="print-button" onclick="window.print()">🖨️ Print All Claims (${selectedRows.length})</button>
            ${allClaimsHTML}
          </body>
        </html>
      `);
      claimWindow.document.close();
    } catch (err) {
      console.error('Error generating multiple claims:', err);
      setError(`Failed to generate claims: ${err.message}`);
      
      if (claimWindow && !claimWindow.closed) {
        claimWindow.document.body.innerHTML = `
          <div style="padding: 40px; text-align: center; color: red;">
            <h2>Error Generating Claims</h2>
            <p>${err.message}</p>
            <button onclick="window.close()" style="margin-top: 20px; padding: 10px 20px; background-color: #dc2626; color: white; border: none; border-radius: 4px; cursor: pointer;">Close Window</button>
          </div>
        `;
      }
    } finally {
      setGeneratingClaim(false);
    }
  };

  const handleGenerateSummaryReport = () => {
    if (!insuranceType || !monthYear) {
      setError('Please select both Insurance Type and Month/Year before generating report');
      return;
    }

    const summaryWindow = window.open('', '_blank', 'width=1400,height=900');
    
    if (summaryWindow) {
      summaryWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Claims Summary Report - ${insuranceType} - ${monthYear}</title>
            <style>
              @page { size: A4 landscape; margin: 5mm; }
              body { font-family: Arial, sans-serif; margin: 20px; padding: 0; }
              .header { text-align: center; margin-bottom: 20px; }
              .header h2 { margin: 5px 0; font-size: 16px; }
              .header h3 { margin: 5px 0; font-size: 14px; }
              .header p { margin: 3px 0; font-size: 12px; }
              table { width: 100%; border-collapse: collapse; font-size: 9px; margin-top: 10px; }
              th, td { border: 1px solid #000; padding: 3px; text-align: left; }
              th { background-color: #D1D5DB; font-weight: bold; text-align: center; }
              td.number { text-align: right; }
              td.center { text-align: center; }
              .total-row { background-color: #E5E7EB; font-weight: bold; }
              .print-button { position: fixed; top: 20px; right: 20px; padding: 10px 20px; background-color: #1E40AF; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; z-index: 1000; }
              .print-button:hover { background-color: #1E3A8A; }
              @media print { .print-button { display: none; } body { margin: 0; } }
            </style>
          </head>
          <body>
            <button class="print-button" onclick="window.print()">🖨️ Print Report</button>
            <div class="header">
              <h2>${insuranceType}</h2>
              <h3>CLAIMS SUMMARY PER ATTENDANCE</h3>
              <h3>MONTH OF SERVICE/CLAIM: ${monthYear}</h3>
              <p><strong>Jitrock Hospital</strong></p>
            </div>
            <div id="report-content"><p>Loading data...</p></div>
          </body>
        </html>
      `);

      axios.get(claimsDetailsall, { params: { insuranceType, monthYear } })
      .then(response => {
        const data = response.data;
        let totals = { lab: 0, service: 0, drugs: 0, total: 0 };

        const tableRows = data.map(row => {
          const age = calculateAge(row.dob);
          const fullName = `${row.surname || ''} ${row.othername || ''}`.trim();
          const lab = parseFloat(row.lab || 0);
          const service = parseFloat(row.main_status || 0);
          const drugs = parseFloat(row.drugs || 0);
          const total = lab + service + drugs;
          
          totals.lab += lab;
          totals.service += service;
          totals.drugs += drugs;
          totals.total += total;

          return `
            <tr>
              <td>${fullName}</td>
              <td class="center">${row.dat}</td>
              <td class="center">${row.d_dat}</td>
              <td class="center">${age}</td>
              <td class="center">${row.gender || ''}</td>
              <td>${row.patient_id || ''}</td>
              <td>${row.insurance_no || ''}</td>
              <td>${row.diagnosis || 'null'}</td>
              <td class="number">${formatWithOutSom(lab)}</td>
              <td class="number">${formatWithOutSom(service)}</td>
              <td class="number">${formatWithOutSom(drugs)}</td>
              <td class="number">${formatWithOutSom(total)}</td>
            </tr>
          `;
        }).join('');

        const tableHTML = `
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Date of Att.</th><th>Date of Dix</th><th>Age</th><th>Sex</th><th>Folder #</th><th>ID NO.</th><th>Diagnosis</th><th>Lab</th><th>Service</th><th>Drugs</th><th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
              <tr class="total-row">
                <td colspan="8" style="text-align: right;"><strong>Total</strong></td>
                <td class="number"><strong>${formatWithOutSom(totals.lab)}</strong></td>
                <td class="number"><strong>${formatWithOutSom(totals.service)}</strong></td>
                <td class="number"><strong>${formatWithOutSom(totals.drugs)}</strong></td>
                <td class="number"><strong>${formatWithOutSom(totals.total)}</strong></td>
              </tr>
            </tbody>
          </table>
          <p style="margin-top: 20px; font-size: 10px;">Generated on: ${new Date().toLocaleString()}</p>
        `;

        summaryWindow.document.getElementById('report-content').innerHTML = tableHTML;
      })
      .catch(error => {
        console.error('Error loading summary data:', error);
        summaryWindow.document.getElementById('report-content').innerHTML = '<p style="color: red;">Error loading data. Please try again.</p>';
      });
    } else {
      setError('Unable to open new window. Please check your browser settings.');
    }
  };

  const handleGenerateGrandReport = () => {
    console.log('Generate Grand Report');
  };

const handleGenerateXMLFile = async () => {
    if (!insuranceType || !monthYear) {
      setError('Please select both Insurance Type and Month/Year before generating XML');
      return;
    }


    if (claimsData.length === 0) {
      setError('No claims data available. Please filter claims first.');
      return;
    }

    // Ask whether this export is for a clinic or a hospital.
    // Clinic exports omit the <investigation> block entirely.
    const facilityChoice = await Swal.fire({
      title: 'Export XML For…',
      html:
        '<p style="color:#475569;font-size:14px;margin:8px 0 4px;">Choose the facility type for this export.</p>' +
        '<p style="color:#64748b;font-size:12px;margin:0 0 4px;">Clinic exports will <strong>omit investigations</strong> from the XML. Hospital exports include everything.</p>',
      icon: 'question',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Hospital',
      denyButtonText: 'Clinic',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#11998e',
      denyButtonColor: '#0ea5e9',
      cancelButtonColor: '#64748b',
      reverseButtons: true,
      focusConfirm: true
    });

    if (facilityChoice.isDismissed) return;
    //  Sent to the server as `kind`. The rule that a clinic export omits the
    //  investigation block lives there now, with the rest of the NHIA shape,
    //  rather than being decided here and applied to a file built here.
    const facilityType = facilityChoice.isDenied ? 'clinic' : 'hospital';

    // ✅ Apply date filter for XML export if dates are set.
    // Old code called .substring(0, 10) directly on row.dat — but that field
    // can arrive as a Date object (mysql2 returns DATETIME as Date) or as an
    // ISO string with a Z suffix. Calling .substring() on a Date threw
    // "is not a function" and blew up the whole export. Normalise to
    // YYYY-MM-DD via toDateString() below so all shapes work.
    const toIsoDay = (v) => {
      if (!v) return '';
      if (v instanceof Date) {
        const y = v.getFullYear();
        const m = String(v.getMonth() + 1).padStart(2, '0');
        const d = String(v.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
      const s = String(v).trim();
      // Handles "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss...", "YYYY-MM-DD HH:mm:ss".
      const iso = s.slice(0, 10);
      return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : '';
    };
    const xmlClaimsData = claimsData.filter(row => {
      if (!xmlStartDate && !xmlEndDate) return true;
      const visitDate = toIsoDay(row.dat);
      if (!visitDate) return false;
      if (xmlStartDate && xmlEndDate) return visitDate >= xmlStartDate && visitDate <= xmlEndDate;
      if (xmlStartDate) return visitDate >= xmlStartDate;
      if (xmlEndDate)   return visitDate <= xmlEndDate;
      return true;
    });

      if (xmlClaimsData.length === 0) {
        setError(`No claims found between ${xmlStartDate || '...'} and ${xmlEndDate || '...'}. Clear the date filter or adjust the range.`);
        return;
      }
    

    // ✅ CREATE FILE HANDLE FIRST - Let user choose where to save
    let fileHandle = null;
    const filename = `Claims_${insuranceType.replace(/\s+/g, '_')}_${monthYear}.xml`;

    try {
      // Request file save location from user BEFORE processing
      if ('showSaveFilePicker' in window) {
        try {
          fileHandle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'XML Files',
              accept: { 'application/xml': ['.xml'] }
            }]
          });
        } catch (err) {
          // User cancelled the save dialog
          if (err.name === 'AbortError') {
            console.log('User cancelled file save');
            return;
          }
          throw err;
        }
      }
    } catch (err) {
      console.error('Error creating file handle:', err);
      setError('Your browser does not support file save picker. File will be downloaded to default location.');
      // Continue without file handle - will use download link fallback
    }

    let progressWindow = null;

    try {
      setGeneratingClaim(true);
      setError(null);

      progressWindow = window.open('', '_blank', 'width=700,height=500');
      
      if (!progressWindow) {
        setError('Unable to open progress window. Please check your browser pop-up blocker settings.');
        setGeneratingClaim(false);
        return;
      }

      progressWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Generating XML - ${insuranceType} - ${monthYear}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              
              @keyframes gradientShift {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
              }
              
              @keyframes shimmer {
                0% { background-position: -200% 0; }
                100% { background-position: 200% 0; }
              }
              
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.8; }
              }
              
              @keyframes scaleIn {
                0% { transform: scale(0) rotate(-180deg); opacity: 0; }
                100% { transform: scale(1) rotate(0deg); opacity: 1; }
              }
              
              @keyframes float {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-10px); }
              }
              
              @keyframes glow {
                0%, 100% { box-shadow: 0 0 20px rgba(17, 153, 142, 0.4), 0 0 40px rgba(56, 239, 125, 0.2); }
                50% { box-shadow: 0 0 30px rgba(17, 153, 142, 0.6), 0 0 60px rgba(56, 239, 125, 0.3); }
              }
              
              body { 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                background: linear-gradient(135deg, #11998e 0%, #38ef7d 50%, #0f7c6f 100%);
                background-size: 200% 200%;
                animation: gradientShift 15s ease infinite;
                min-height: 100vh;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 20px;
                overflow: hidden;
              }
              
              body::before {
                content: '';
                position: fixed;
                top: -50%;
                left: -50%;
                width: 200%;
                height: 200%;
                background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
                background-size: 50px 50px;
                animation: float 20s linear infinite;
                opacity: 0.3;
              }
              
              .container { 
                background: rgba(255, 255, 255, 0.98);
                backdrop-filter: blur(20px) saturate(180%);
                border-radius: 24px;
                padding: 50px 45px;
                box-shadow: 
                  0 30px 90px rgba(0, 0, 0, 0.3),
                  0 0 0 1px rgba(255, 255, 255, 0.5) inset,
                  0 0 100px rgba(17, 153, 142, 0.2);
                max-width: 600px;
                width: 100%;
                position: relative;
                z-index: 10;
              }
              
              .icon-wrapper {
                text-align: center;
                margin-bottom: 25px;
              }
              
              .file-icon {
                font-size: 56px;
                animation: float 3s ease-in-out infinite;
                display: inline-block;
                filter: drop-shadow(0 4px 12px rgba(17, 153, 142, 0.3));
              }
              
              h2 { 
                color: #1e293b;
                margin-bottom: 8px;
                font-size: 28px;
                font-weight: 800;
                text-align: center;
                background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
              }
              
              .subtitle { 
                color: #64748b;
                margin-bottom: 35px;
                font-size: 15px;
                text-align: center;
                font-weight: 600;
                letter-spacing: 0.5px;
              }
              
              .progress-section {
                margin: 35px 0;
              }
              
              .progress-bar-wrapper {
                position: relative;
                margin-bottom: 25px;
              }
              
              .progress-bar-container { 
                width: 100%;
                height: 24px;
                background: linear-gradient(180deg, #e2e8f0 0%, #cbd5e1 100%);
                border-radius: 100px;
                overflow: visible;
                position: relative;
                box-shadow: 
                  inset 0 2px 4px rgba(0, 0, 0, 0.1),
                  inset 0 -1px 2px rgba(255, 255, 255, 0.5),
                  0 4px 12px rgba(0, 0, 0, 0.1);
                border: 2px solid rgba(255, 255, 255, 0.8);
              }
              
              .progress-bar { 
                height: 100%;
                background: linear-gradient(135deg, 
                  #11998e 0%, 
                  #38ef7d 50%, 
                  #11998e 100%
                );
                background-size: 300% 100%;
                width: 0%;
                border-radius: 100px;
                transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                animation: shimmer 3s linear infinite;
                position: relative;
                box-shadow: 
                  0 4px 16px rgba(17, 153, 142, 0.5),
                  0 2px 8px rgba(56, 239, 125, 0.4),
                  inset 0 1px 0 rgba(255, 255, 255, 0.4);
                overflow: hidden;
              }
              
              .progress-bar::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(
                  90deg,
                  transparent,
                  rgba(255, 255, 255, 0.4) 50%,
                  transparent
                );
                animation: shine 2s infinite;
              }
              
              @keyframes shine {
                0% { left: -100%; }
                100% { left: 200%; }
              }
              
              .progress-orb {
                position: absolute;
                top: 50%;
                right: -8px;
                width: 32px;
                height: 32px;
                background: radial-gradient(circle, #fff 0%, #11998e 100%);
                border-radius: 50%;
                transform: translateY(-50%);
                box-shadow: 
                  0 0 20px rgba(17, 153, 142, 0.8),
                  0 0 40px rgba(56, 239, 125, 0.4),
                  inset 0 -2px 4px rgba(0, 0, 0, 0.2);
                animation: glow 2s ease-in-out infinite;
                border: 3px solid rgba(255, 255, 255, 0.9);
              }
              
              .progress-percentage-wrapper {
                display: flex;
                align-items: center;
                justify-content: center;
                margin-top: 20px;
                gap: 15px;
              }
              
              .progress-percentage {
                font-size: 48px;
                font-weight: 900;
                background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
                text-shadow: 0 2px 8px rgba(17, 153, 142, 0.2);
                letter-spacing: -2px;
                position: relative;
              }
              
              .percentage-symbol {
                font-size: 24px;
                color: #94a3b8;
                font-weight: 700;
              }
              
              .progress-text { 
                margin-top: 25px;
                font-size: 17px;
                color: #1e293b;
                font-weight: 700;
                text-align: center;
                letter-spacing: 0.3px;
              }
              
              .status-text { 
                margin-top: 10px;
                font-size: 14px;
                color: #64748b;
                text-align: center;
                min-height: 22px;
                font-weight: 500;
              }
              
              .stats-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 18px;
                margin-top: 30px;
              }
              
              .stat-card {
                background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                border-radius: 16px;
                padding: 20px;
                text-align: center;
                box-shadow: 
                  0 4px 12px rgba(0, 0, 0, 0.05),
                  inset 0 1px 0 rgba(255, 255, 255, 0.8);
                border: 1px solid rgba(255, 255, 255, 0.6);
                position: relative;
                overflow: hidden;
                transition: transform 0.3s ease;
              }
              
              .stat-card::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(
                  90deg,
                  transparent,
                  rgba(17, 153, 142, 0.1),
                  transparent
                );
                transition: left 0.5s;
              }
              
              .stat-card:hover::before {
                left: 100%;
              }
              
              .stat-value {
                font-size: 36px;
                font-weight: 900;
                background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
                margin-bottom: 6px;
                line-height: 1;
              }
              
              .stat-label {
                font-size: 12px;
                color: #64748b;
                text-transform: uppercase;
                letter-spacing: 1px;
                font-weight: 700;
              }
              
              .speed-indicator {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                margin-top: 15px;
                padding: 10px 20px;
                background: linear-gradient(135deg, rgba(17, 153, 142, 0.1) 0%, rgba(56, 239, 125, 0.1) 100%);
                border-radius: 100px;
                border: 1px solid rgba(17, 153, 142, 0.2);
              }
              
              .speed-indicator-dot {
                width: 8px;
                height: 8px;
                background: #10b981;
                border-radius: 50%;
                animation: pulse 1.5s ease-in-out infinite;
                box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
              }
              
              .speed-indicator-text {
                font-size: 13px;
                color: #64748b;
                font-weight: 600;
              }
              
              .success-icon { 
                font-size: 80px;
                margin: 25px 0;
                text-align: center;
                animation: scaleIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
                filter: drop-shadow(0 8px 24px rgba(16, 185, 129, 0.3));
              }
              
              .success-checkmark {
                display: inline-block;
                width: 80px;
                height: 80px;
                border-radius: 50%;
                background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                position: relative;
                box-shadow: 0 8px 32px rgba(16, 185, 129, 0.4);
              }
              
              .success-checkmark::after {
                content: '✓';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                font-size: 48px;
                font-weight: bold;
              }
              
              .download-button { 
                margin-top: 30px;
                padding: 16px 40px;
                background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
                color: white;
                border: none;
                border-radius: 14px;
                cursor: pointer;
                font-size: 17px;
                font-weight: 700;
                box-shadow: 
                  0 8px 24px rgba(17, 153, 142, 0.4),
                  inset 0 1px 0 rgba(255, 255, 255, 0.2);
                transition: all 0.3s ease;
                text-decoration: none;
                display: inline-block;
                width: 100%;
                letter-spacing: 0.5px;
                border: 2px solid rgba(255, 255, 255, 0.2);
              }
              
              .download-button:hover { 
                transform: translateY(-3px);
                box-shadow: 
                  0 12px 32px rgba(17, 153, 142, 0.5),
                  inset 0 1px 0 rgba(255, 255, 255, 0.3);
              }
              
              .download-button:active {
                transform: translateY(-1px);
              }
              
              .error-icon {
                font-size: 80px;
                color: #ef4444;
                margin: 25px 0;
                text-align: center;
                animation: scaleIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
                filter: drop-shadow(0 8px 24px rgba(239, 68, 68, 0.3));
              }
              
              .file-details {
                background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                border-radius: 16px;
                padding: 24px;
                margin: 25px 0;
                box-shadow: 
                  0 4px 12px rgba(0, 0, 0, 0.05),
                  inset 0 1px 0 rgba(255, 255, 255, 0.8);
                border: 1px solid rgba(255, 255, 255, 0.6);
              }
              
              .file-details-title {
                font-size: 18px;
                color: #1e293b;
                font-weight: 700;
                margin-bottom: 10px;
              }
              
              .file-details-subtitle {
                font-size: 15px;
                color: #64748b;
                margin-bottom: 15px;
              }
              
              .file-details-name {
                font-size: 13px;
                color: #94a3b8;
                font-family: 'Courier New', monospace;
                background: rgba(17, 153, 142, 0.05);
                padding: 10px 15px;
                border-radius: 8px;
                border: 1px solid rgba(17, 153, 142, 0.1);
                word-break: break-all;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="icon-wrapper">
                <div class="file-icon">📄</div>
              </div>
              
              <h2>Generating XML Claims File</h2>
              <p class="subtitle">${insuranceType} • ${monthYear}</p>
              
              <div class="progress-section">
                <div class="progress-bar-wrapper">
                  <div class="progress-bar-container">
                    <div class="progress-bar" id="progressBar">
                      <div class="progress-orb"></div>
                    </div>
                  </div>
                </div>
                
                <div class="progress-percentage-wrapper">
                  <span class="progress-percentage" id="progressPercentage">0</span>
                  <span class="percentage-symbol">%</span>
                </div>
              </div>
              
              <div class="progress-text" id="progressText">Initializing...</div>
              <div class="status-text" id="statusText">Preparing data...</div>
              
              <div class="speed-indicator">
                <div class="speed-indicator-dot"></div>
                <span class="speed-indicator-text">Processing at high speed</span>
              </div>
              
              <div class="stats-grid">
                <div class="stat-card">
                  <div class="stat-value" id="processedCount">0</div>
                  <div class="stat-label">Processed</div>
                </div>
                <div class="stat-card">
                  <div class="stat-value" id="totalCount">${xmlClaimsData.length}</div>
                  <div class="stat-label">Total Claims</div>
                </div>
              </div>
            </div>
          </body>
        </html>
      `);
      progressWindow.document.close();

      const updateProgress = (percentage, text, status = '', processed = 0) => {
        if (!progressWindow || progressWindow.closed) return;
        
        const progressBar = progressWindow.document.getElementById('progressBar');
        const progressPercentage = progressWindow.document.getElementById('progressPercentage');
        const progressText = progressWindow.document.getElementById('progressText');
        const statusText = progressWindow.document.getElementById('statusText');
        const processedCount = progressWindow.document.getElementById('processedCount');
        
        if (progressBar) progressBar.style.width = percentage + '%';
        if (progressPercentage) progressPercentage.textContent = percentage;
        if (progressText) progressText.textContent = text;
        if (statusText) statusText.textContent = status;
        if (processedCount) processedCount.textContent = processed;
      };

      updateProgress(5, 'Building XML structure...', 'Asking the server for the file', 0);

      // ══════════════════════════════════════════════════════════════════════
      //  THE XML IS BUILT ON THE SERVER NOW, NOT HERE
      // ══════════════════════════════════════════════════════════════════════
      //
      //  WHAT THIS REPLACES, AND WHY
      //
      //  This function used to fetch four endpoints per claim and assemble NHIA
      //  XML in the browser. The September submission built that way came back
      //  with 456 of 616 claims REJECTED, and the report named the reasons:
      //
      //    366 claims  "GDRG ... is not applicable"
      //    248 claims  "GDRG ... is invalid"
      //
      //  Both were this builder's doing, and neither was a small slip:
      //
      //    · specialtyAttended was which_code.substring(0, 4) while the diagnosis
      //      gdrgCode came from a different column entirely. On claim 10303 that
      //      shipped specialty OPDC against G-DRG MEDI31A, and NHIA answered
      //      "the GDRG may not be allowed for the specialties attended". The two
      //      values had no reason to agree, so mostly they did not.
      //
      //    · the G-DRG lost its trailing letter — MEDI28A left as MEDI28, which
      //      is not a code NHIA knows ("the formatting of the code is likely to
      //      be wrong", in its own words).
      //
      //    · one code was reused for every diagnosis on the claim. Claim 10306
      //      holds Malaria (MEDI28A) and Peptic Ulcer Disease (PAED34C); both
      //      went out as MEDI28.
      //
      //    · serviceOutcome used DIED / TFOT / ABSC. NHIA's codes are DEATH and
      //      TROUT.
      //
      //    · dateOfService was emitted twice unconditionally, so an OPD claim
      //      with no discharge date carried an empty second element.
      //
      //  backend/routes/claimsXmlExport.js already gets all of this right, for
      //  imported AND hospital claims: it resolves a principal G-DRG, emits a
      //  specialty that matches it, and omits elements it has no value for. It is
      //  the exporter behind the claims screen, it has five test files, and it
      //  was already reading the hospital tables through claimStore.flatEnvelopes.
      //  The only thing it could not do was a whole MONTH of typed-in claims,
      //  which is exactly why this second builder existed. That gap is closed
      //  (model=facility), so there is one exporter again instead of two that
      //  drift apart.
      //
      //  Keeping a second XML writer in the browser is what caused this. It is
      //  not being repaired in place — it is being removed.
      const totalClaims = xmlClaimsData.length;
      let processedClaims = 0;

      const [expYear, expMonth] = String(monthYear || '').split('-');

      updateProgress(25, 'Building XML structure...', 'Resolving G-DRGs and specialties', 0);

      const exportResponse = await fetch(claimsXmlExport, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          //  The hospital's own claims: claim_information plus the ward tables.
          model: 'facility',
          year: expYear,
          month: expMonth,
          insurance: insuranceType,
          //  'clinic' drops the investigation block, which is the same question
          //  the dialog above asked. The server owns that rule too now.
          kind: facilityType,
          //  Narrow WITHIN the month, matching the date filter on screen.
          from: xmlStartDate || undefined,
          to: xmlEndDate || undefined,
        }),
      });

      if (!exportResponse.ok) {
        //  409 is the deliberate refusal: the server could not assemble every
        //  claim in the month and will not hand over a file that is short
        //  without saying so. Show its reason rather than a generic failure —
        //  submitting a partial batch is the expensive mistake here.
        let detail = '';
        try {
          const problem = await exportResponse.json();
          detail = problem.error || problem.message || '';
          if (problem.hint) detail += ` ${problem.hint}`;
        } catch (e) {
          detail = `The server returned ${exportResponse.status}.`;
        }
        throw new Error(detail || `Export failed (${exportResponse.status}).`);
      }

      const xmlContent = await exportResponse.text();
      //  What the server actually wrote, not what the screen guessed. If these
      //  disagree the officer needs to know before submitting.
      const written = Number(exportResponse.headers.get('X-Claim-Count'));
      processedClaims = Number.isFinite(written) && written > 0 ? written : totalClaims;

      updateProgress(95, 'Finalizing file...', 'Almost done', processedClaims);


      // ✅ SAVE FILE - Use file handle if available, otherwise download
      updateProgress(98, 'Saving file...', 'Writing to disk', processedClaims);

      if (fileHandle) {
        // User selected a location - save directly there
        try {
          const writable = await fileHandle.createWritable();
          await writable.write(xmlContent);
          await writable.close();
          
          updateProgress(100, 'File saved successfully!', 'Completed', processedClaims);
        } catch (err) {
          console.error('Error writing file:', err);
          throw new Error('Failed to save file to selected location');
        }
      } else {
        // Fallback to download link
        const blob = new Blob([xmlContent], { type: 'application/xml' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        
        updateProgress(100, 'Download complete!', 'File saved to downloads folder', processedClaims);
      }

      // Show success message
      if (progressWindow && !progressWindow.closed) {
        setTimeout(() => {
          const saveLocation = fileHandle ? 'your selected location' : 'your downloads folder';
          progressWindow.document.body.innerHTML = `
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: linear-gradient(135deg, #11998e 0%, #38ef7d 50%, #0f7c6f 100%); background-size: 200% 200%; animation: gradientShift 15s ease infinite; min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px;">
              <style>
                @keyframes gradientShift {
                  0% { background-position: 0% 50%; }
                  50% { background-position: 100% 50%; }
                  100% { background-position: 0% 50%; }
                }
                @keyframes scaleIn {
                  0% { transform: scale(0) rotate(-180deg); opacity: 0; }
                  100% { transform: scale(1) rotate(0deg); opacity: 1; }
                }
              </style>
              <div style="background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(20px); border-radius: 24px; padding: 50px 45px; box-shadow: 0 30px 90px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.5) inset; max-width: 600px; width: 100%; text-align: center;">
                <div style="display: inline-block; width: 80px; height: 80px; border-radius: 50%; background: linear-gradient(135deg, #10b981 0%, #059669 100%); position: relative; box-shadow: 0 8px 32px rgba(16, 185, 129, 0.4); margin: 25px 0; animation: scaleIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);">
                  <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: white; font-size: 48px; font-weight: bold;">✓</div>
                </div>
                
                <h2 style="color: #1e293b; margin-bottom: 8px; font-size: 28px; font-weight: 800; background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">File Saved Successfully!</h2>
                <p style="color: #64748b; margin-bottom: 30px; font-size: 15px; font-weight: 600;">Processed ${processedClaims} claims and saved to ${saveLocation}</p>
                
                <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-radius: 16px; padding: 24px; margin: 25px 0; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8); border: 1px solid rgba(255, 255, 255, 0.6);">
                  <div style="font-size: 18px; color: #1e293b; font-weight: 700; margin-bottom: 10px;">${insuranceType}</div>
                  <div style="font-size: 15px; color: #64748b; margin-bottom: 15px;">${monthYear}</div>
                  <div style="font-size: 13px; color: #94a3b8; font-family: 'Courier New', monospace; background: rgba(17, 153, 142, 0.05); padding: 10px 15px; border-radius: 8px; border: 1px solid rgba(17, 153, 142, 0.1); word-break: break-all;">${filename}</div>
                </div>
                
                <button onclick="window.close()" style="margin-top: 30px; padding: 16px 40px; background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%); color: white; border: none; border-radius: 14px; cursor: pointer; font-size: 17px; font-weight: 700; box-shadow: 0 8px 24px rgba(17, 153, 142, 0.4); width: 100%; letter-spacing: 0.5px; border: 2px solid rgba(255, 255, 255, 0.2);">Close Window</button>
              </div>
            </div>
          `;
        }, 500);
      }

      console.log(`XML file generated with ${processedClaims} claims and saved`);

    } catch (err) {
      console.error('Error generating XML file:', err);
      setError(`Failed to generate XML file: ${err.message}`);
      
      if (progressWindow && !progressWindow.closed) {
        progressWindow.document.body.innerHTML = `
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: linear-gradient(135deg, #11998e 0%, #38ef7d 50%, #0f7c6f 100%); min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px;">
            <div style="background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(20px); border-radius: 24px; padding: 50px 45px; box-shadow: 0 30px 90px rgba(0, 0, 0, 0.3); max-width: 600px; width: 100%; text-align: center;">
              <div style="font-size: 80px; color: #ef4444; margin: 25px 0; filter: drop-shadow(0 8px 24px rgba(239, 68, 68, 0.3));">✗</div>
              <h2 style="color: #ef4444; margin-bottom: 8px; font-size: 28px; font-weight: 800;">Error Generating XML</h2>
              <p style="color: #64748b; margin: 20px 0; font-size: 14px;">${err.message}</p>
              <button onclick="window.close()" style="margin-top: 30px; padding: 16px 40px; background: #ef4444; color: white; border: none; border-radius: 14px; cursor: pointer; font-size: 17px; font-weight: 700; width: 100%; letter-spacing: 0.5px;">Close Window</button>
            </div>
          </div>
        `;
      }
    } finally {
      setGeneratingClaim(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id)
        ? prev.filter((rowId) => rowId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = claimsData.map((row) => row.code);
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };


//  convertFrequency mapped free-text frequencies ("twice daily") to the NHIA
//  prescription codes ("2 DAILY") for the in-browser XML builder. That builder
//  is gone and the server does this through prescriptionText.prescriptionFor,
//  so this copy is removed rather than left to drift from the one in use.



  
  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Paper elevation={3}>
        <HeaderBox>All Claims Details</HeaderBox>

        <FilterSection>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Insurance Type</InputLabel>
            <Select value={insuranceType} onChange={(e) => setInsuranceType(e.target.value)} label="Insurance Type" size="small">
              <MenuItem value=""><em>Select Insurance</em></MenuItem>
              {insuranceTypes.map((type) => (
                <MenuItem key={type.scheme_name} value={type.scheme_name}>{type.scheme_name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Month/Year</InputLabel>
            <Select value={monthYear} onChange={(e) => setMonthYear(e.target.value)} label="Month/Year" size="small">
              <MenuItem value=""><em>Select Month/Year</em></MenuItem>
              {claimMonths.map((month) => (
                <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
            <SecondaryButton variant="contained" onClick={handleGenerateSummaryReport}>Summary Report</SecondaryButton>
            <StyledButton variant="contained" onClick={handleGenerateGrandReport}>Grand Report</StyledButton>
            <SecondaryButton variant="contained" onClick={handleGenerateXMLFile} disabled={generatingClaim || claimsData.length === 0} sx={{ backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}>
              {generatingClaim ? (<><CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />Generating...</>) : ('📄 Generate XML')}
            </SecondaryButton>
          </Box>
        </FilterSection>

        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <StyledButton variant="contained" onClick={fetchClaimsData}>Filter All Claims</StyledButton>
          <SecondaryButton variant="contained" onClick={handlePreviewSelectedClaims} disabled={selectedRows.length === 0 || generatingClaim}>
            {generatingClaim ? (<><CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />Generating...</>) : (`Preview Selected (${selectedRows.length})`)}
          </SecondaryButton>
          {selectedRows.length > 0 && (
            <Typography variant="body2" color="text.secondary">{selectedRows.length} claim{selectedRows.length > 1 ? 's' : ''} selected</Typography>
          )}

          {/* ✅ XML Date Range Filter */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>
              XML Period:
            </Typography>
            <input
              type="date"
              value={xmlStartDate}
              onChange={(e) => setXmlStartDate(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '13px', color: '#374151' }}
            />
            <Typography variant="body2" sx={{ color: '#6b7280' }}>to</Typography>
            <input
              type="date"
              value={xmlEndDate}
              onChange={(e) => setXmlEndDate(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '13px', color: '#374151' }}
            />
            {(xmlStartDate || xmlEndDate) && (
              <Button
                size="small"
                onClick={() => { setXmlStartDate(''); setXmlEndDate(''); }}
                sx={{ textTransform: 'none', color: '#dc2626', fontSize: '12px', minWidth: 'auto', px: 1 }}
              >
                ✕ Clear
              </Button>
            )}
            <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '11px' }}>
              {xmlStartDate || xmlEndDate ? '(XML export will use this period)' : '(Optional: filter XML by date)'}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Box sx={{ px: 2, py: 1 }}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading claims data...</Typography>
          </Box>
        )}

        {!loading && claimsData.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableHeadCell padding="checkbox">
                      <Checkbox checked={claimsData.length > 0 && selectedRows.length === claimsData.length} onChange={handleSelectAll} />
                    </StyledTableHeadCell>
                    <StyledTableHeadCell>ID</StyledTableHeadCell>
                    <StyledTableHeadCell>Insurance Status</StyledTableHeadCell>
                    <StyledTableHeadCell>Insurance NO</StyledTableHeadCell>
                    <StyledTableHeadCell>Patient Id</StyledTableHeadCell>
                    <StyledTableHeadCell>Name</StyledTableHeadCell>
                    <StyledTableHeadCell>Gender</StyledTableHeadCell>
                    <StyledTableHeadCell>Age</StyledTableHeadCell>
                    <StyledTableHeadCell>Physician Name</StyledTableHeadCell>
                    <StyledTableHeadCell>Main Status</StyledTableHeadCell>
                    <StyledTableHeadCell>Visit Date</StyledTableHeadCell>
                    <StyledTableHeadCell>Discharge Date</StyledTableHeadCell>
                    <StyledTableHeadCell>Report</StyledTableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {claimsData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <TableRow key={row.code} hover selected={selectedRows.includes(row.code)}>
                      <StyledTableCell padding="checkbox">
                        <Checkbox checked={selectedRows.includes(row.code)} onChange={() => handleSelectRow(row.code)} />
                      </StyledTableCell>
                      <StyledTableCell>{row.code}</StyledTableCell>
                      <StyledTableCell>{row.scheme_name}</StyledTableCell>
                      <StyledTableCell>{row.insurance_no}</StyledTableCell>
                      <StyledTableCell>{row.patient_id}</StyledTableCell>
                      <StyledTableCell>{`${row.surname || ''} ${row.othername || ''}`.trim()}</StyledTableCell>
                      <StyledTableCell>{row.gender}</StyledTableCell>
                      <StyledTableCell>{calculateAge(row.dob)}</StyledTableCell>
                      <StyledTableCell>{row.physician_name}</StyledTableCell>
                      <StyledTableCell>{row.which}</StyledTableCell>
                      <StyledTableCell>{row.dat}</StyledTableCell>
                      <StyledTableCell>{row.d_dat}</StyledTableCell>
                      <StyledTableCell>
                        <IconButton size="small" color="primary" onClick={() => handleGenerateIndividualClaim(row)} title="Generate Individual Claim" disabled={generatingClaim}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination component="div" count={claimsData.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} rowsPerPageOptions={[10, 25, 50, 100]} />
          </>
        )}

        {!loading && claimsData.length === 0 && insuranceType && monthYear && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No claims found for the selected criteria</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ClaimsReport;